import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Sprout, ShieldCheck, Leaf } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    const err = params.get("error");
    if (err) toast.error(err);
  }, [params]);

  useEffect(() => {
    api.get("/public/tenant").then(r => setTenant(r.data)).catch(() => setTenant({ project: null }));
  }, []);

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === "customer" ? "/portal" : "/dashboard");
    }
  }, [user, loading, navigate]);

  const signIn = () => {
    setBusy(true);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const brandName = tenant?.project?.company_name || tenant?.project?.name || "Sanjeevani Estates";
  const subtitle = tenant?.project
    ? `Welcome to ${brandName}. Only invited emails for this project may sign in.`
    : "Sign in with the Google account you were invited with. Unknown emails will be turned away.";
  const logoUrl = tenant?.project?.has_logo ? `${api.defaults.baseURL}/projects/${tenant.project.id}/logo` : null;

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-5 bg-background">
      <div className="hidden lg:block lg:col-span-3 relative">
        <img
          src="https://images.unsplash.com/photo-1720155390935-f49e18525eb9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBmYXJtbGFuZCUyMGFlcmlhbCUyMHZpZXd8ZW58MHx8fHwxNzkwMjQ1Nzg4fDA&ixlib=rb-4.1.0&q=85"
          alt="Farmland aerial"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={`${brandName} logo`} className="h-10 w-10 rounded-md object-contain bg-white/10 border border-white/20 p-1"/>
            ) : (
              <div className="h-10 w-10 rounded-md bg-white/10 border border-white/20 grid place-items-center backdrop-blur-sm">
                <Sprout className="h-5 w-5" strokeWidth={1.5} />
              </div>
            )}
            <span className="font-heading font-semibold tracking-tight text-lg">{brandName}</span>
          </div>
          <div className="max-w-xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              {brandName} — maintenance, ordered.
            </h1>
            <p className="mt-6 text-white/85 text-base leading-relaxed max-w-lg">
              A grounded system for the farmland collective to manage co-owners, plot cycles, and maintenance billing — with the calm rigor of good bookkeeping.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
              <div className="border border-white/20 rounded-md p-4 bg-white/5 backdrop-blur-sm">
                <ShieldCheck className="h-5 w-5 mb-2" strokeWidth={1.5} />
                <div className="text-sm font-medium">PII encrypted at rest</div>
              </div>
              <div className="border border-white/20 rounded-md p-4 bg-white/5 backdrop-blur-sm">
                <Leaf className="h-5 w-5 mb-2" strokeWidth={1.5} />
                <div className="text-sm font-medium">Cycle-aware billing</div>
              </div>
            </div>
          </div>
          <div className="text-xs text-white/60">© {new Date().getFullYear()} · {brandName}</div>
        </div>
      </div>
      <div className="col-span-1 lg:col-span-2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            {logoUrl ? <img src={logoUrl} alt="" className="h-6 w-6 object-contain"/> : <Sprout className="h-6 w-6 text-primary" strokeWidth={1.5} />}
            <span className="font-heading text-lg font-semibold">{brandName}</span>
          </div>
          <div className="mb-2 text-xs font-heading uppercase tracking-widest text-muted-foreground">Sign in</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground">Welcome back.</h2>
          <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{subtitle}</p>
          <div className="mt-10 space-y-4">
            <Button
              data-testid="google-signin-btn"
              onClick={signIn}
              disabled={busy}
              size="lg"
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            >
              <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24" aria-hidden>
                <path fill="#EA4335" d="M12 10.2v3.9h5.45c-.24 1.4-1.75 4.1-5.45 4.1-3.28 0-5.96-2.72-5.96-6.08S8.72 6.04 12 6.04c1.86 0 3.11.79 3.83 1.47l2.61-2.52C16.78 3.51 14.6 2.6 12 2.6 6.98 2.6 2.94 6.64 2.94 11.66S6.98 20.72 12 20.72c6.94 0 8.94-4.86 8.24-9.41H12z"/>
              </svg>
              Continue with Google
            </Button>
            <div className="text-xs text-muted-foreground pt-6 border-t border-border">
              First time? Ask your administrator to invite your Google email.
              {tenant?.project && <> You must be assigned to <b>{brandName}</b> specifically.</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
